import React, { useEffect, useMemo, useRef } from 'react'
import CustomModal from '../../baseComponents/gui/customModal/CustomModal'
import { safeHTML } from '../../../utils/safeHTML'
import { converterModalContent, converterResultModal } from '../../../constants/copyright-modals'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import Icon from '../../baseComponents/gui/icon/Icon'
import { useModal } from '../../../hooks/useModal'
import { useUser } from '../../../redux/reducer/user'
import numWord from '../../../utils/numWord'
import { useRequestsHandlerData } from '../../../hooks/useRequestsHandlerData'
import AppLoader from '../../features/appLoader/AppLoader'
import Picture from '../../baseComponents/gui/picture/Picture'
import { useContent } from '../../../redux/reducer/content'
import { useTranslation } from '../../../translations'
import { useFeatureToggle } from '../../../hooks/useFeatureToggle'
import { useErrorModalHandler } from '../../../hooks/error/useErrorModalHandler'

const utils = {
  getGiftText: (count) => {
    return `${numWord(count, ['other.gift.text1', 'other.gift.text2', 'other.gift.text3'])}`
  },
  getBeautiesText: (count) => {
    return `${numWord(count, [
      'other.beautiesVariants.text1',
      'other.beautiesVariants.text2',
      'other.beautiesVariants.text3',
    ])}`
  },
}

const ConverterModal = React.forwardRef(function ConverterModal({ callbacks }, ref) {
  const { profile } = useUser()
  const { convertData } = useContent()
  const { addModal, closeModal, id } = useModal()
  const { t } = useTranslation()
  const showErrorModal = useErrorModalHandler()

  const prevEnergy = useRef(profile.attempts)
  const tempProfile = useRef(null)
  const beautiesBalanceOnmountCall = useRef(true)

  const { title, text, note, warning, converterIcons, buttons, maxConvertPerDay } =
    converterModalContent

  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')
  const giftCondition = !gameTamagotchiBeauties

  const activeCash = useMemo(() => {
    if (giftCondition) {
      if (profile?.gacha_event_cash === null || profile?.gacha_event_cash === undefined) return ''

      return profile?.gacha_event_cash
    } else {
      if (profile?.beauties === null || profile?.beauties === undefined) return ''

      return profile?.beauties
    }
  }, [giftCondition, profile?.gacha_event_cash, profile?.beauties])

  const activeCashText = useMemo(() => {
    if (activeCash === '') return ''

    if (giftCondition) {
      return `${t('other.gifts')}: ${activeCash}`
    } else {
      return `${t('other.beauties')}: ${activeCash}`
    }
  }, [giftCondition, activeCash, t])

  // Контент для модального окна конвертации
  const modalData = {
    // Картинка что меняем
    convertImage: giftCondition ? converterIcons.gift.imgAttr : converterIcons.beauties.imgAttr,
    // Данные сколько меняем
    convertValue: giftCondition
      ? `${convertData?.proportion} ${t(utils.getGiftText(convertData?.proportion))}`
      : `${convertData?.proportion} ${t(utils.getBeautiesText(convertData?.proportion))}`,
    // Что меняем
    convertText: giftCondition ? text.gift : text.beauties,
    // Баланс пользователя (того что меняем)
    activeCash: activeCashText,
    // Сколько максимум в день можно совершить обмена
    maxConvertPerDay: giftCondition ? t(maxConvertPerDay.gift) : t(maxConvertPerDay.beauties),
  }

  // Условия при которых конвертация невозможна
  // 1. лимит конвертаций превышен (2 в день)
  // 2. недостаточно гифтов или бьютисов
  const isButtonDisabled = useMemo(() => {
    return (
      convertData.converted >= convertData.limit ||
      (giftCondition
        ? profile?.gacha_event_cash < convertData.proportion
        : profile?.beauties < convertData.proportion)
    )
  }, [
    convertData?.proportion,
    convertData?.converted,
    convertData?.limit,
    profile?.gacha_event_cash,
    profile?.beauties,
  ])

  // Текст с пояснениями если конвертация невозможна
  const _warning = useMemo(() => {
    const converted = convertData.converted
    const limit = convertData.limit
    return giftCondition
      ? warning.gifts(converted, limit, profile?.gacha_event_cash, convertData.proportion)
      : warning.beauties(converted, limit, profile?.beauties, convertData.proportion)
  }, [
    convertData?.proportion,
    convertData?.converted,
    convertData?.limit,
    profile?.gacha_event_cash,
    profile?.beauties,
  ])

  const hasWarnig = useMemo(() => {
    return !!_warning
  }, [_warning])

  const buttonCallbacks = {
    exchange: () => {
      if (gameTamagotchiBeauties) {
        beautiesBalanceOnmountCall.current = false
      }

      callbacks.fromGachaToAttempts()
    },
    cancel: () => {
      closeModal(id)
    },
  }

  // Модальное окно при успешной конвертации
  // Значение баланса гифтов берется из полученного profile в ответе при fromGachaToAttempts() fulfilled
  // Значение баланса бьютисов запрашивается в getBeautiesBalance() и обновляется сразу в сторе profile (useUser())
  const openWModal = () => {
    const diff = tempProfile.current.attempts - prevEnergy.current

    const { description, rewards, noteValue, ...props } = converterResultModal
    const converted = convertData.converted + 1
    const limit = convertData.limit

    const buttons = props.buttons
      .map((button) => {
        return {
          ...button,
          attr: {
            ...button.attr,
            text: button.attr.text(converted, limit),
            disabled:
              button.goConverter &&
              (converted >= limit || tempProfile.current.gacha_event_cash < convertData.proportion),
          },
        }
      })
      .filter((el) => el.attr.disabled !== true)

    const _description = giftCondition
      ? description.gifts(
          converted,
          limit,
          tempProfile.current.gacha_event_cash,
          convertData.proportion,
        )
      : description.beauties(converted, limit, profile?.beauties, convertData.proportion)
    addModal({
      type: 'wModal',
      props: {
        ...props,
        buttons,
        callbacks,
        description: _description
          .replace('{convert}', `${limit - converted}`)
          .replace(
            '{truthWord}',
            numWord(diff, [
              'converterModalContent.converter.0.text',
              'converterModalContent.converter.1.text',
              'converterModalContent.converter.2.text',
            ]),
          ),
        noteValue: giftCondition ? noteValue.gifts : noteValue.beauties,
        giftsBalance: giftCondition ? profile?.gacha_event_cash : profile?.beauties,
        rewards: rewards.map((item) => ({ ...item, value: diff.toString() })),
      },
    })
  }

  const isPending = useRequestsHandlerData([
    {
      address: 'requestsGachaList',
    },
    {
      address: 'requestsUserAttempts',
      fulfilled: ({ profile }) => {
        tempProfile.current = profile

        // При конвертации проверяем если фт gameTamagotchiBeauties true
        // то дополнительно запрашиваем getBeautiesBalance для получения актуального баланса бьютисов
        if (gameTamagotchiBeauties) {
          callbacks.getBeautiesBalance()
        } else {
          openWModal()
        }
      },
    },

    {
      address: 'requestsFromGachaToAttempts',
      fulfilled: () => {
        // При успешной конвертации энергии запрашиваем актуальный баланс энергии
        callbacks.getAttempts()
      },
      settled: () => {
        if (!gameTamagotchiBeauties) {
          callbacks.getLootBoxesList()
          closeModal(id)
        }
      },
      rejected: (data) => {
        const { response } = data

        showErrorModal(response.data)
      },
    },
    {
      address: 'requestsUserBeautiesBalance',
      fulfilled: () => {
        if (gameTamagotchiBeauties && beautiesBalanceOnmountCall.current) return

        openWModal()
      },
      settled: () => {
        if (gameTamagotchiBeauties && beautiesBalanceOnmountCall.current) return

        callbacks.getLootBoxesList()
        closeModal(id)
      },
    },
  ])

  useEffect(() => {
    if (gameTamagotchiBeauties) {
      callbacks.getBeautiesBalance()
    }
  }, [])

  return (
    <CustomModal>
      <div
        className={'converter-modal'}
        ref={ref}
      >
        <div className={'converter-modal__bg'} />
        <div className={'converter-modal__content'}>
          <h2 className={'converter-modal__title'}>{safeHTML(t(title.new))}</h2>

          <p className={'converter-modal__text'}>
            {safeHTML(t(modalData.convertText, { maxConvertPerDay: modalData.maxConvertPerDay }))}
          </p>

          <p className={'converter-modal__gifts'}>{modalData.activeCash}</p>
          <p className={'converter-modal__note'}>{safeHTML(t(note.text))}</p>
          <div className={'converter-modal__converter'}>
            <div className={'converter-modal__input'}>
              <div className={'converter-modal__input-image'}>
                <Picture imgAttr={modalData.convertImage} />
              </div>
              <div className={'converter-modal__input-value'}>{modalData.convertValue}</div>
            </div>
            <div className={'converter-modal__arrow'}>
              <Icon name={'arrow-right'} />
            </div>
            <div className={'converter-modal__result'}>
              <div className={'converter-modal__result-image'}>
                <Picture {...converterIcons.energy} />
              </div>
              <div className={'converter-modal__result-value'}>{`+1 ${t('other.energy')}`}</div>
            </div>
          </div>
          {hasWarnig && <p className={'converter-modal__warning'}>{safeHTML(t(_warning))}</p>}
          <div className={`converter-modal__buttons`}>
            {buttons.map(({ action, attr }) => (
              <CustomButton
                key={action}
                {...{ ...attr, text: attr.text(convertData?.converted, convertData?.limit) }}
                onClick={buttonCallbacks[action]}
                disabled={action === 'exchange' && isButtonDisabled}
              />
            ))}
          </div>
        </div>
      </div>
      {isPending && <AppLoader />}
    </CustomModal>
  )
})
export default ConverterModal
ConverterModal.propTypes = {}
